import { useDirectories } from "../hooks/useDirectories";
import { Box, CircularProgress, Typography, IconButton } from "@mui/material";
import { useState } from "react";
import FolderCard from "../components/FolderCard";
import CardDocument from "../components/CardDocument";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DirectoryType, Document } from "../../common/redux/documentsAction";
import { ContainerDocument, EmptyDataBox } from "./MyDocuments";
import CustomSnackbar from "../../common/components/CustomSnackbar";
import { useTranslation } from "react-i18next";
import EmptyDataDocs from "./EmptyDataDocs";
import { useDirectory } from "../hooks/useDirectory";
import { useAppSelector } from "../../common/redux/reduxHooks";
import { RootState } from "../../common/redux/store";

const SharedWithMeDocuments = () => {
    const [activeDirectoryId, setActiveDirectoryId] = useState<string | null>(
        null
    );
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const structureUserSelector = useAppSelector(
        (state: RootState) => state.user.data
    );
    const professionalUserSelector = useAppSelector(
        (state: RootState) => state.user.proData
    );
    const userId = structureUserSelector?.id || professionalUserSelector?.id;

    const { directories, loading: loadingDirectories } = useDirectories({
        type: DirectoryType.SHARED,
        "owner.id": `not:${userId}`,
    });
    const { directory, loading: loadingDirectory } =
        useDirectory(activeDirectoryId);
    const loading = loadingDirectories || loadingDirectory;

    const { t } = useTranslation();

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const viewDocument = (document: Document) => {
        window.open(document.fileUri, "_blank");
    };

    const download = (doc: Document) => {
        const link = document.createElement("a");
        link.href = doc.fileUri;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={8}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box mt={2} p={1}>
            <Typography variant="h6">
                {t("manageDocuments.title.section.sharedwithme")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                {t("manageDocuments.subtitle.section.sharedwithme", {
                    count: directory?.documents.length ?? 0,
                    dirCount: directories?.length ?? 0,
                })}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                {t("manageDocuments.description.section.sharedwithme")}
            </Typography>
            {directory ? (
                <>
                    <Box display="flex" alignItems="center" mb={2} mt={4}>
                        <IconButton onClick={() => setActiveDirectoryId(null)}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" ml={1}>
                            {directory.owner?.name ?? directory.name}
                        </Typography>
                    </Box>
                    <ContainerDocument>
                        {directory.documents.length === 0 ? (
                            <EmptyDataBox>
                                <EmptyDataDocs
                                    title={t(
                                        "manageDocuments.empty.sharedwithme.data.file"
                                    )}
                                    subtitle=""
                                />
                            </EmptyDataBox>
                        ) : (
                            directory.documents.map((document) => (
                                <Box key={document.id}>
                                    <CardDocument
                                        document={document}
                                        onView={viewDocument}
                                        onDownload={download}
                                    />
                                </Box>
                            ))
                        )}
                    </ContainerDocument>
                </>
            ) : (
                <>
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        gap={4}
                        margin="1rem 0 4rem 0"
                    >
                        {directories.length === 0 ? (
                            <EmptyDataBox>
                                <EmptyDataDocs
                                    title={t(
                                        "manageDocuments.empty.sharedwithme.data.title"
                                    )}
                                    subtitle={t(
                                        "manageDocuments.empty.sharedwithme.data.subtitle"
                                    )}
                                />
                            </EmptyDataBox>
                        ) : (
                            directories.map(({ id, name, owner }) => (
                                <FolderCard
                                    key={id}
                                    name={owner?.name ?? name}
                                    onClick={() => setActiveDirectoryId(id)}
                                />
                            ))
                        )}
                    </Box>
                </>
            )}
            {/* <ConfirmationModal
                open={!!docToDelete}
                onClose={() => setDocToDelete(null)}
                title={t("manageDocuments.delete.modal.title")}
                message={t("manageDocuments.delete.modal.body", {
                    name: docToDelete?.name,
                })}
                onValidation={handleDeleteConfirm}
            />
            <RenameModal
                open={renameModalOpen}
                initialValue={docToRename?.name ?? ""}
                onClose={() => {
                    setRenameModalOpen(false);
                    setDocToRename(null);
                }}
                onConfirm={handleRenameConfirm}
            /> */}
            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity as any}
                handleCloseSnackbar={handleCloseSnackbar}
            />
        </Box>
    );
};

export default SharedWithMeDocuments;
